import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Link2, CheckCircle2, AlertCircle, ExternalLink, ArrowRight } from "lucide-react";

/**
 * The bookmarklet popup. URL: /alldata-bridge/pick?url=...&title=...
 * Lists active capture sessions for the logged-in user and lets them
 * attach the captured page URL to one of them.
 */
export default function AllDataBridgePick() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(null);
  const [done, setDone] = useState(null);

  const url = params.get("url") || "";
  const title = params.get("title") || "";

  useEffect(() => {
    if (!user) {
      // Not logged in — bounce to login with a return param
      const next = `/alldata-bridge/pick?${params.toString()}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    api.get("/alldata-bridge/pending-sessions")
      .then((r) => setSessions(r.data || []))
      .catch(() => toast.error("Could not load sessions"))
      .finally(() => setLoading(false));
  }, [user, navigate, params]);

  const attach = async (s) => {
    setAttaching(s.token);
    try {
      const r = await api.post("/alldata-bridge/finalize", {
        token: s.token, url, title,
      });
      setDone({ session: s, result: r.data });
      toast.success("Attached to TrueLine!");
      // Auto-close popup after 1.5s
      setTimeout(() => {
        try { window.close(); } catch (e) { /* noop */ }
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach");
    } finally {
      setAttaching(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-200 p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-200 p-6 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-display text-2xl text-emerald-300 mb-2">Captured!</h1>
          <p className="text-sm text-slate-400 mb-1">Attached to:</p>
          <p className="text-base text-slate-200 font-medium">{done.session.card_name}</p>
          <p className="text-xs text-slate-500 mt-1">{done.session.vehicle_label}</p>
          <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 mt-3">
            {(done.result.captured_source || "manual").toUpperCase()}
          </Badge>
          <p className="text-xs text-slate-600 mt-6 italic">This window will close automatically...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-400 mb-2">
            ALLDATA BRIDGE — PICKER
          </div>
          <h1 className="font-display text-2xl font-semibold">Send to TrueLine</h1>
          <p className="text-sm text-slate-400 mt-1">
            Choose which Calibration Report card to attach this page to.
          </p>
        </div>

        {/* Captured page */}
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 mb-6" data-testid="bridge-captured-page">
          <div className="text-[10px] uppercase tracking-wider font-bold text-blue-300 mb-2 flex items-center gap-1.5">
            <Link2 className="w-3 h-3" /> Captured Page
          </div>
          <div className="text-sm font-medium text-slate-200 mb-1">{title || "(no title)"}</div>
          <div className="text-xs text-slate-400 font-mono truncate">{url || "(no url)"}</div>
        </div>

        {/* Sessions list */}
        {sessions.length === 0 ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center">
            <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-3" />
            <h3 className="font-display text-lg text-rose-300 mb-2">No active capture sessions</h3>
            <p className="text-sm text-slate-400 mb-4 leading-relaxed">
              Open a Calibration Report → click <strong className="text-amber-300">🎯 Auto-Capture</strong> on the
              operation card → then come back and click the bookmarklet again.
            </p>
            <Button
              onClick={() => { try { window.opener?.focus(); } catch (e) {} window.location.href = "/calibration-reports"; }}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="bridge-go-reports-btn"
            >
              Open Calibration Reports <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="bridge-sessions-list">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              {sessions.length} Active Session{sessions.length === 1 ? "" : "s"}
            </div>
            {sessions.map((s) => (
              <button
                key={s.token}
                onClick={() => attach(s)}
                disabled={attaching}
                data-testid={`bridge-session-${s.token}`}
                className="w-full text-left rounded-xl border border-slate-800 bg-[#0B1120] hover:border-emerald-500/50 hover:bg-emerald-500/5 transition p-4 group disabled:opacity-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-slate-100 truncate">
                      {s.card_name || "(unknown card)"}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{s.vehicle_label || "—"}</div>
                    <div className="text-[10px] text-slate-600 mt-1.5 font-mono">
                      Token: {s.token.slice(0, 8)}… · Expires {new Date(s.expires_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="flex items-center shrink-0">
                    {attaching === s.token ? (
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    ) : (
                      <span className="px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] uppercase tracking-wider font-bold group-hover:bg-emerald-500/25">
                        Attach here →
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800 text-[11px] text-slate-600 italic text-center">
          You can close this window anytime — only the click on &quot;Attach here&quot; sends the URL to TrueLine.
        </div>
      </div>
    </div>
  );
}
